






































import Vue from 'vue';
import Cookie from 'js-cookie';
import { DateTime } from 'luxon';
import axios from 'axios';

import { Prop, Component } from 'vue-property-decorator';

import { ScreenType, ActionType, SlidesIds } from '@/config/globalVariables';
import { slidesData } from '@/models/SlideModel';

import { ISlide, IAction, IMessage } from '@/common/types';

import * as log from 'loglevel';

import { Translate } from '@/lang/Translator';

import { gaSend } from '@/common/googleAnalytics';

log.setLevel(0);

// components
import QuestionForm from './QuestionForm.vue';

@Component({
  components: { QuestionForm },
})
export default class Screencast extends Vue {
  // props
  @Prop() private value: number;

  // data
  private slides: ISlide[] = [];
  private currentScreenId = this.value;
  private localTime: string;

  private formWithPhone: boolean = false;
  private isCallMeBack: boolean = false;

  // computed
  get t(): Translate {
    return this.$store.state.t;
  }

  get currentScreen(): ISlide | undefined {
    if (this.slides) {
      const slide = this.slides.find((slideItem: ISlide) => slideItem.id === this.currentScreenId);
      if (slide) {
        this.$emit('input', slide.id);
      }
      return slide;
    }

    this.$emit('input', undefined);
    return undefined;
  }

  get conciergeMessage(): string[] {
    if (this.currentScreen && this.currentScreen.message) {
      if (Array.isArray(this.currentScreen.message)) {
        return this.currentScreen.message;
      }

      return [this.currentScreen.message];
    }

    return [];
  }

  get screenActions(): IAction[] {
    if (this.currentScreen && this.currentScreen.type !== ScreenType.request_a_call) {
      return this.currentScreen.actions;
    }
    return [];
  }

  get isRequestACallScreen(): boolean {
    if (this.currentScreen) {
      return this.currentScreen.type === ScreenType.request_a_call;
    }
    return false;
  }

  get isTYPContactFormScreen(): boolean {
    if (this.currentScreen) {
      return this.currentScreen.id === SlidesIds.TYPContactForm;
    }
    return false;
  }

  get additionalClassesToButton(): object {
    if (this.currentScreen) {
      return {
        inline: this.currentScreen!.type === ScreenType.inline_buttons,
      };
    }

    return {};
  }

  get languageApp(): string {
    return this.$store.state.lang;
  }

  get isGlobalVersionSite(): boolean {
    return this.$store.state.isGlobal;
  }

  // methods
  private loadSlides(): void {
    slidesData.forEach((slideItem: ISlide): void => {
      const slideActions: IAction[] = [];

      slideItem.actions.forEach((actionData: IAction) => {
        const action: IAction = {
          text: this.t.text(actionData.text),
          type: actionData.type,
          moveTo: actionData.moveTo,
        };

        if (actionData.addCookie) {
          action.addCookie = actionData.addCookie;
        }

        if (actionData.onlyFor) {
          action.onlyFor = actionData.onlyFor;
        }

        if (actionData.id) {
          action.id = actionData.id;
        }

        if (actionData.moveToRegional) {
          action.moveToRegional = actionData.moveToRegional;
        }

        slideActions.push(action);
      });

      const newSlide: ISlide = {
        id: slideItem.id,
        type: slideItem.type,
        messageKey: slideItem.messageKey,
        message: this.t.text(slideItem.messageKey),
        actions: slideActions,
      };

      if (slideItem.dialogs) {
        newSlide.dialogs = slideItem.dialogs;
      }

      this.slides.push(newSlide);
    });
  }

  private async checkChatStatus(): Promise<boolean> {
    let status = false;

    if (this.languageApp === 'en') {
      try {
        const url = `${process.env.VUE_APP_OPERATOR_API_DOMAIN}/operators/get-chat-status?lang=${this.languageApp}`;
        const response = await axios.get(url);
        if (response) {
          status = Boolean(response.data);
        }
      } catch (e) {
        log.error('checkChatStatus(): failed request', e);
      }
    }

    return status;
  }

  private async clickToButton(item: IAction): Promise<void> {
    gaSend(item.text, window.location.pathname);

    switch (item.type) {
      case ActionType.moveTo:
        if (typeof item.moveTo === 'number') {
          if (item.id === `${SlidesIds.howToAnswer}-phone`) {
            this.formWithPhone = true;
            this.isCallMeBack = true;
          }

          this.currentScreenId = item.moveTo;
        }

        break;

      case ActionType.redirect: {
        if (typeof item.moveTo === 'string') {
          // set cookie for show TYP slide on the next page
          if (item.addCookie && item.addCookie.startFrom) {
            let jsonCookieData: Array<{ url: string, page: number }> = Cookie.getJSON('fromAssistant');
            if (jsonCookieData === undefined) {
              jsonCookieData = [];
            }

            // update fromAssistant[]
            jsonCookieData.push({ url: item.moveTo, page: item.addCookie.startFrom });
            Cookie.remove('fromAssistant');
            Cookie.set('fromAssistant', jsonCookieData);
          }

          // redirect to custom regional page
          if (item.moveToRegional && item.moveToRegional.get(this.languageApp)) {
            window.location.href = item.moveToRegional.get(this.languageApp) || '' ;
            break;
          }

          let addLangPath = '';
          const demoLangs = ['de', 'fr'];

          if (
            this.currentScreenId === SlidesIds.you_were_redirected
            && demoLangs.indexOf(this.languageApp) !== -1
            && item.id === 'product_online_demo'
          ) {
            addLangPath = this.languageApp + '/';
          }

          window.location.href = `${addLangPath}${item.moveTo}`;
        }
        break;
      }
      case ActionType.startChat:
        log.info('ActionType.startChat');
        const status = await this.checkChatStatus();
        log.info(`Chat status: ${status}`);

        if (status && !this.isGlobalVersionSite) {
          // load chat
          item.text = '⏱️ Connecting...';

          gaSend('Started', window.location.pathname);

          this.$emit('chat-request');
        } else {
          // create Lead Question
          if (typeof item.moveTo === 'number') {
            // move to choice how to answer (email||phone)
            if (this.languageApp === 'en') {
              this.currentScreenId = SlidesIds.howToAnswer;
            } else {
              this.currentScreenId = item.moveTo;
            }
          }
        }

        break;
    }
  }

  private showTYPContactForm(clientMessageData: any): void {
    this.currentScreenId = SlidesIds.TYPContactForm;

    Vue.nextTick()
      .then(() => {
        if (this.currentScreen && this.currentScreen.dialogs && SlidesIds.TYPContactForm) {

          const dt = DateTime.local().setLocale('en-US');
          const localTime = dt.toLocaleString(DateTime.TIME_SIMPLE) || '';

          const clientMessage: IMessage = {
            text: clientMessageData.message,
            time: localTime,
            fname: clientMessageData.fname,
            lname: clientMessageData.lname,
            isAdmin: false,
          };

          const conciergeMessage: IMessage = {
            text: this.t.text(`${SlidesIds.TYPContactForm}-response`),
            time: localTime,
            fname: 'Netwrix',
            lname: 'Concierge',
            isAdmin: true,
          };

          this.currentScreen.dialogs.push(clientMessage);
          this.currentScreen.dialogs.push(conciergeMessage);
        }
      });
  }

  private getStartScreenId(): number {
    let startScreen = 0;

    const getData = Cookie.getJSON('fromAssistant');
    if (getData) {
      const currentPage = window.location.pathname;
      const pageIndex = getData.findIndex((item: { url: string, page: number }) => `/${item.url}` === currentPage);
      if (pageIndex !== -1) {
        const pageInfo = getData[pageIndex];

        startScreen = pageInfo.page;
        getData.splice(pageIndex, 1);

        Cookie.remove('fromAssistant');
        Cookie.set('fromAssistant', getData);
      }
    }
    return startScreen;
  }

  private created(): void {
    this.loadSlides();

    this.currentScreenId = this.getStartScreenId();

    const dt = DateTime.local().setLocale('en-US');
    this.localTime = dt.toLocaleString(DateTime.TIME_SIMPLE) || '';
  }
}
